import { frontendConfig } from '@kiku/config/env/frontend';
import { createFileRoute } from '@tanstack/react-router';
import { LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '~/components/ui/button';
import { useDocumentTitle } from '~/hooks/use-document-title';

const apiBase = frontendConfig.VITE_API_BASE_URL.replace(/\/$/, '');

const loginSearchSchema = z.object({
  error: z.enum(['not_provisioned', 'missing_code', 'session_expired']).optional()
});

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  component: LoginPage
});

function LoginPage(): React.ReactNode {
  const { t } = useTranslation();
  const { error } = Route.useSearch();
  useDocumentTitle(t('login.cta'));
  const [loginPopup, setLoginPopup] = useState<Window | null>(null);
  const inIframe = window.self !== window.top;

  useEffect(() => {
    if (!loginPopup) return;
    const interval = setInterval(() => {
      if (loginPopup.closed) {
        setLoginPopup(null);
        clearInterval(interval);
        return;
      }
      fetch(`${apiBase}/api/auth/me`, { credentials: 'include' })
        .then((res) => {
          if (res.ok) {
            loginPopup.close();
            window.location.href = '/';
          }
        })
        .catch(() => undefined);
    }, 1500);
    return () => {
      clearInterval(interval);
    };
  }, [loginPopup]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="rounded-2xl border border-border bg-card px-10 py-12 shadow-sm">
          {/* Logo */}
          <div className="mb-8 flex flex-col items-center gap-4">
            <img src="/kiku_logo.png" alt="Kiku" className="h-10 w-auto" />
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-foreground">{t('login.title')}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{t('login.subtitle')}</p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {t(`login.errors.${error}`)}
            </div>
          )}

          {/* CTA */}
          <Button
            className="w-full"
            disabled={loginPopup !== null}
            onClick={() => {
              if (inIframe) {
                const popup = window.open(
                  `${apiBase}/auth/login`,
                  'kiku-login',
                  'width=480,height=640,resizable=yes,scrollbars=yes'
                );
                setLoginPopup(popup);
              } else {
                window.location.href = `${apiBase}/auth/login`;
              }
            }}
          >
            <LogIn className="size-4" />
            {loginPopup !== null ? t('login.waitingForLogin') : t('login.cta')}
          </Button>

          {/* Sign out link when account isn't provisioned */}
          {error === 'not_provisioned' && (
            <p className="mt-4 text-center text-sm text-muted-foreground">
              <a
                href={`${apiBase}/auth/logout`}
                className="underline underline-offset-4 hover:text-foreground"
              >
                {t('nav.logout')}
              </a>
            </p>
          )}

          {/* EU AI Act disclosure */}
          <p className="mt-6 text-center text-xs leading-relaxed text-muted-foreground">
            {t('login.disclosure')}
          </p>
        </div>

        {/* Footer wordmark */}
        <p className="mt-6 text-center text-xs text-muted-foreground/50">
          &copy; {new Date().getFullYear()} Kiku
        </p>
      </div>
    </div>
  );
}
